import { Button } from '@aqarmap/aqarmap-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Modal from '~/components/Modal';
import PaymentOption from '~/components/PurchasePaymentMethod/PaymentOption';
import { useFullRoutes } from '~/hooks';
import { closeModal } from '~/redux/features/uiState/uiStateSlice';
import { useAppDispatch } from '~/redux/hooks';

export type PublishAccountType = 'mainAccount' | 'myAccount';

export default function PublishAccountModal({
  fees,
  canPayFees,
  isLoading,
  onConfirm,
}: {
  fees: number;
  canPayFees: boolean;
  isLoading: boolean;
  onConfirm: (account: PublishAccountType) => void;
}) {
  const { t } = useTranslation('app');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { PACKAGES_URL } = useFullRoutes();
  const [selectedAccount, setSelectedAccount] =
    useState<PublishAccountType | null>(null);

  useEffect(() => {
    if (!canPayFees) setSelectedAccount('mainAccount');
  }, [canPayFees]);

  return (
    <Modal
      modalKey="publishAccountModal"
      title={t('publishAccountTitle')}
      scrolled
      Footer={
        <div className="flex w-full gap-x sm:pb-x">
          <Button
            ariaLabel="pay-publish-listing"
            size="medium"
            variant="primary"
            className="w-full whitespace-nowrap"
            isLoading={isLoading}
            disabled={!selectedAccount || isLoading}
            onClick={() => selectedAccount && onConfirm(selectedAccount)}
          >
            {t('publishAccountPayNowCredits', { credits: fees ?? 0 })}
          </Button>
          <Button
            ariaLabel="cancel-publish-listing"
            size="medium"
            variant="secondary"
            className="w-full"
            disabled={isLoading}
            onClick={() => dispatch(closeModal('publishAccountModal'))}
          >
            {t('cancel')}
          </Button>
        </div>
      }
      bodyClassName="!pt-2x"
    >
      <div className="flex flex-col gap-2x">
        <PaymentOption
          radioName="publishAccount"
          checked={selectedAccount === 'mainAccount'}
          onClick={() => setSelectedAccount('mainAccount')}
          className="cursor-pointer pb-2x border-b border-gray"
          title={
            <div className="flex flex-col gap-x">
              <p className="text-title-4">{t('mainAccount')}</p>
              <p className="text-caption-1 text-gray__dark_2">
                {t('mainAccountPublishDesc')}
              </p>
            </div>
          }
        />
        <PaymentOption
          radioName="publishAccount"
          checked={selectedAccount === 'myAccount'}
          onClick={() => setSelectedAccount('myAccount')}
          disabled={!canPayFees}
          className={canPayFees ? 'cursor-pointer' : 'cursor-default'}
          title={
            <div className="flex flex-col gap-x w-full">
              <p className="text-title-4">{t('myAccount')}</p>
              <p className="text-caption-1 text-gray__dark_2">
                {t('myAccountPublishDesc')}
              </p>
              {!canPayFees && (
                <div className="flex justify-between items-center gap-x mt-0.5x px-1.5x py-x rounded-[8px] bg-orange_light_2">
                  <p className="text-body-2 text-orange_dark_2">
                    {t('insufficientBalance')}
                  </p>
                  <Button
                    ariaLabel="charge-credit-publish-listing"
                    size="small"
                    variant="link"
                    className="!p-0 !h-auto text-link text-blue whitespace-nowrap no-underline hover:no-underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(closeModal('publishAccountModal'));
                      navigate(PACKAGES_URL.ROOT);
                    }}
                  >
                    {t('subscribeToPackage')}
                  </Button>
                </div>
              )}
            </div>
          }
        />
      </div>
    </Modal>
  );
}
